import isArray from 'lodash/isArray';
import reduce from 'lodash/reduce';
import { sanitizeEntity } from './sanitize';
import { types as sdkTypes } from './sdkLoader';
import OpenAI from 'openai';
import { jsPDF } from 'jspdf';
import listView from '../assets/images/tree.png';
import { pricings } from '../config/configListing';
const { Money } = sdkTypes;
// NOTE: This file imports sanitize.js, which may lead to circular dependency

/**
 * Combine the given relationships objects
 *
 * See: http://jsonapi.org/format/#document-resource-object-relationships
 */
export const combinedRelationships = (oldRels, newRels) => {
  if (!oldRels && !newRels) {
    // Special case to avoid adding an empty relationships object when
    // none of the resource objects had any relationships.
    return null;
  }
  return { ...oldRels, ...newRels };
};

/**
 * Combine the given resource objects
 *
 * See: http://jsonapi.org/format/#document-resource-objects
 */
export const combinedResourceObjects = (oldRes, newRes) => {
  const { id, type } = oldRes;
  if (newRes.id.uuid !== id.uuid || newRes.type !== type) {
    throw new Error('Cannot merge resource objects with different ids or types');
  }
  const attributes = newRes.attributes || oldRes.attributes;
  const attributesOld = oldRes.attributes || {};
  const attributesNew = newRes.attributes || {};
  // Allow (potentially) sparse attributes to update only relevant fields
  const attrs = attributes ? { attributes: { ...attributesOld, ...attributesNew } } : null;
  const relationships = combinedRelationships(oldRes.relationships, newRes.relationships);
  const rels = relationships ? { relationships } : null;
  return { id, type, ...attrs, ...rels };
};

/**
 * Combine the resource objects form the given api response to the
 * existing entities.
 */
export const updatedEntities = (oldEntities, apiResponse, sanitizeConfig = {}) => {
  const { data, included = [] } = apiResponse;
  const objects = (Array.isArray(data) ? data : [data]).concat(included);

  const newEntities = objects.reduce((entities, curr) => {
    const { id, type } = curr;

    // Some entities (e.g. listing and user) might include extended data,
    // you should check if src/util/sanitize.js needs to be updated.
    const current = sanitizeEntity(curr, sanitizeConfig);

    entities[type] = entities[type] || {};
    const entity = entities[type][id.uuid];
    entities[type][id.uuid] = entity ? combinedResourceObjects({ ...entity }, current) : current;

    return entities;
  }, oldEntities);

  return newEntities;
};

/**
 * Denormalise the entities with the resources from the entities object
 *
 * This function calculates the dernormalised tree structure from the
 * normalised entities object with all the relationships joined in.
 *
 * @param {Object} entities entities object in the SDK Redux store
 * @param {Array<{ id, type }} resources array of objects
 * with id and type
 * @param {Boolean} throwIfNotFound wheather to skip a resource that
 * is not found (false), or to throw an Error (true)
 *
 * @return {Array} the given resource objects denormalised that were
 * found in the entities
 */
export const denormalisedEntities = (entities, resources, throwIfNotFound = true) => {
  const denormalised = resources.map(res => {
    const { id, type } = res;
    const entityFound = entities[type] && id && entities[type][id.uuid];
    if (!entityFound) {
      if (throwIfNotFound) {
        throw new Error(`Entity with type "${type}" and id "${id ? id.uuid : id}" not found`);
      }
      return null;
    }
    const entity = entities[type][id.uuid];
    const { relationships, ...entityData } = entity;

    if (relationships) {
      // Recursively join in all the relationship entities
      return reduce(
        relationships,
        (ent, relRef, relName) => {
          // A relationship reference can be either a single object or
          // an array of objects. We want to keep that form in the final
          // result.
          const hasMultipleRefs = Array.isArray(relRef.data);
          const multipleRefsEmpty = hasMultipleRefs && relRef.data.length === 0;
          if (!relRef.data || multipleRefsEmpty) {
            ent[relName] = hasMultipleRefs ? [] : null;
          } else {
            const refs = hasMultipleRefs ? relRef.data : [relRef.data];

            // If a relationship is not found, an Error should be thrown
            const rels = denormalisedEntities(entities, refs, true);

            ent[relName] = hasMultipleRefs ? rels : rels[0];
          }
          return ent;
        },
        entityData
      );
    }
    return entityData;
  });
  return denormalised.filter(e => !!e);
};

/**
 * Denormalise the data from the given SDK response
 *
 * @param {Object} sdkResponse response object from an SDK call
 *
 * @return {Array} entities in the response with relationships
 * denormalised from the included data
 */
export const denormalisedResponseEntities = sdkResponse => {
  const apiResponse = sdkResponse.data;
  const data = apiResponse.data;
  const resources = Array.isArray(data) ? data : [data];

  if (!data || resources.length === 0) {
    return [];
  }

  const entities = updatedEntities({}, apiResponse);
  return denormalisedEntities(entities, resources);
};

/**
 * Denormalize JSON object.
 * NOTE: Currently, this only handles denormalization of image references
 *
 * @param {JSON} data from Asset API (e.g. page asset)
 * @param {JSON} included array of asset references (currently only images supported)
 * @returns deep copy of data with images denormalized into it.
 */
const denormalizeJsonData = (data, included) => {
  let copy;

  // Handle strings, numbers, booleans, null
  if (data === null || typeof data !== 'object') {
    return data;
  }

  // At this point the data has typeof 'object' (aka Array or Object)
  // Array is the more specific case (of Object)
  if (data instanceof Array) {
    copy = data.map(datum => denormalizeJsonData(datum, included));
    return copy;
  }

  // Generic Objects
  if (data instanceof Object) {
    copy = {};
    Object.entries(data).forEach(([key, value]) => {
      // Handle denormalization of image reference
      const hasImageRefAsValue =
        typeof value == 'object' &&
        value._ref &&
        value._ref?.type === 'imageAsset' &&
        value._ref?.id;
      // If there is no image included,
      // the _ref might contain parameters for image resolver (Asset Delivery API resolves image URLs on the fly)
      const hasUnresolvedImageRef =
        typeof value == 'object' && value._ref && value._ref?.resolver === 'image';

      if (hasImageRefAsValue) {
        const foundRef = included.find(inc => inc.id === value._ref?.id);
        copy[key] = foundRef;
      } else if (hasUnresolvedImageRef) {
        // Don't add faulty image ref
        // Note: At the time of writing, assets can expose resolver configs,
        //       which we don't want to deal with.
      } else {
        copy[key] = denormalizeJsonData(value, included);
      }
    });
    return copy;
  }

  throw new Error("Unable to traverse data! It's not JSON.");
};

/**
 * Denormalize asset json from Asset API.
 * @param {JSON} assetJson in format: { data, included }
 * @returns deep copy of asset data with images denormalized into it.
 */
export const denormalizeAssetData = assetJson => {
  const { data, included } = assetJson || {};
  return denormalizeJsonData(data, included);
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} transaction entity object, which is to be ensured against null values
 */
export const ensureTransaction = (transaction, booking = null, listing = null, provider = null) => {
  const empty = {
    id: null,
    type: 'transaction',
    attributes: {},
    booking,
    listing,
    provider,
  };
  return { ...empty, ...transaction };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} booking entity object, which is to be ensured against null values
 */
export const ensureBooking = booking => {
  const empty = { id: null, type: 'booking', attributes: {} };
  return { ...empty, ...booking };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureListing = listing => {
  const empty = {
    id: null,
    type: 'listing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} listing entity object, which is to be ensured against null values
 */
export const ensureOwnListing = listing => {
  const empty = {
    id: null,
    type: 'ownListing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} user entity object, which is to be ensured against null values
 */
export const ensureUser = user => {
  const empty = { id: null, type: 'user', attributes: { profile: {} } };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} current user entity object, which is to be ensured against null values
 */
export const ensureCurrentUser = user => {
  const empty = { id: null, type: 'currentUser', attributes: { profile: {} }, profileImage: {} };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} time slot entity object, which is to be ensured against null values
 */
export const ensureTimeSlot = timeSlot => {
  const empty = { id: null, type: 'timeSlot', attributes: {} };
  return { ...empty, ...timeSlot };
};

const PROVINCE_NAME_TO_CODE = {
  alberta: 'AB',
  'british columbia': 'BC',
  manitoba: 'MB',
  'new brunswick': 'NB',
  'newfoundland and labrador': 'NL',
  'nova scotia': 'NS',
  'northwest territories': 'NT',
  nunavut: 'NU',
  ontario: 'ON',
  'prince edward island': 'PE',
  quebec: 'QC',
  saskatchewan: 'SK',
  yukon: 'YT',
};

/**
 * Extract profile address from current user (public data)
 */
export const userProfileAddress = currentUser => {
  const publicData = currentUser?.attributes?.profile?.publicData || {};
  const accountType = publicData.accountType;

  const getFromLocation = () => {
    const address = publicData.location?.search || '';
    if (!address) return { state: '', country: '' };

    const parts = address.split(',').map(p => p.trim().toLowerCase());

    const countryPart = parts.length > 0 ? parts[parts.length - 1] : '';
    const provincePart = parts.length > 1 ? parts[parts.length - 2] : '';

    const countryCode = countryPart === 'canada' ? 'CA' : '';
    const provinceCode = PROVINCE_NAME_TO_CODE[provincePart] || '';

    return { state: provinceCode, country: countryCode };
  };
  return getFromLocation();
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureDayAvailabilityPlan = availabilityPlan => {
  const empty = { type: 'availability-plan/day', entries: [] };
  return { ...empty, ...availabilityPlan };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} availability exception entity object, which is to be ensured against null values
 */
export const ensureAvailabilityException = availabilityException => {
  const empty = { id: null, type: 'availabilityException', attributes: {} };
  return { ...empty, ...availabilityException };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensureStripeCustomer = stripeCustomer => {
  const empty = { id: null, type: 'stripeCustomer', attributes: {} };
  return { ...empty, ...stripeCustomer };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {Object} stripeCustomer entity from API, which is to be ensured against null values
 */
export const ensurePaymentMethodCard = stripePaymentMethod => {
  const empty = {
    id: null,
    type: 'stripePaymentMethod',
    attributes: { type: 'stripe-payment-method/card', card: {} },
  };
  const cardPaymentMethod = { ...empty, ...stripePaymentMethod };

  if (cardPaymentMethod.attributes.type !== 'stripe-payment-method/card') {
    throw new Error(`'ensurePaymentMethodCard' got payment method with wrong type.
      'stripe-payment-method/card' was expected, received ${cardPaymentMethod.attributes.type}`);
  }

  return cardPaymentMethod;
};

/**
 * Get the display name of the given user as string. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned or deleted users, a translated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayNameAsString = (user, defaultUserDisplayName) => {
  const hasAttributes = user && user.attributes;
  const hasProfile = hasAttributes && user.attributes.profile;
  const hasDisplayName = hasProfile && user.attributes.profile.displayName;

  if (hasDisplayName) {
    return user.attributes.profile.displayName;
  } else {
    return defaultUserDisplayName || '';
  }
};

/**
 * DEPRECATED: Use userDisplayNameAsString function or UserDisplayName component instead
 *
 * @param {propTypes.user} user
 * @param {String} bannedUserDisplayName
 *
 * @return {String} display name that can be rendered in the UI
 */
export const userDisplayName = (user, bannedUserDisplayName) => {
  console.warn(
    `Function userDisplayName is deprecated!
User function userDisplayNameAsString or component UserDisplayName instead.`
  );

  return userDisplayNameAsString(user, bannedUserDisplayName);
};

/**
 * Get the abbreviated name of the given user. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned  or deleted users, a default abbreviated name should be provided.
 *
 * @param {propTypes.user} user
 * @param {String} defaultUserAbbreviatedName
 *
 * @return {String} abbreviated name that can be rendered in the UI
 * (e.g. in Avatar initials)
 */
export const userAbbreviatedName = (user, defaultUserAbbreviatedName) => {
  const hasAttributes = user && user.attributes;
  const hasProfile = hasAttributes && user.attributes.profile;
  const hasDisplayName = hasProfile && user.attributes.profile.abbreviatedName;

  if (hasDisplayName) {
    return user.attributes.profile.abbreviatedName;
  } else {
    return defaultUserAbbreviatedName || '';
  }
};

/**
 * A customizer function to be used with the
 * mergeWith function from lodash.
 *
 * Works like merge in every way exept that on case of
 * an array the old value is completely overridden with
 * the new value.
 *
 * @param {Object} objValue Value of current field, denoted by key
 * @param {Object} srcValue New value
 * @param {String} key Key of the field currently being merged
 * @param {Object} object Target object that is receiving values from source
 * @param {Object} source Source object that is merged into object param
 * @param {Object} stack Tracks merged values
 *
 * @return {Object} New value for objValue if the original is an array,
 * otherwise undefined is returned, which results in mergeWith using the
 * standard merging function
 */
export const overrideArrays = (objValue, srcValue, key, object, source, stack) => {
  if (isArray(objValue)) {
    return srcValue;
  }
};

/**
 * Humanizes a line item code. Strips the "line-item/" namespace
 * definition from the beginnign, replaces dashes with spaces and
 * capitalizes the first character.
 *
 * @param {string} code a line item code
 *
 * @return {string} returns the line item code humanized
 */
export const humanizeLineItemCode = code => {
  if (!/^line-item\/.+/.test(code)) {
    throw new Error(`Invalid line item code: ${code}`);
  }
  const lowercase = code.replace(/^line-item\//, '').replace(/-/g, ' ');

  return lowercase.charAt(0).toUpperCase() + lowercase.slice(1);
};

export const parseToFromSharetribe = (data, configData = [], configKey, price) => {
  if (configData && Array.isArray(configData) && configData.length && data && Array.isArray(data) && data.length) {
    const initialValues = {};
    const updatedData = configData.filter((cd, i) => {
      const index = data.findIndex(t => t.key == cd.key);
      if (index > -1) {
        if (data[index].inputValues && Object.keys(data[index].inputValues).length) {
          for (const key in data[index].inputValues) {
            if (Object.hasOwnProperty.call(data[index].inputValues, key)) {
              const element = data[index].inputValues[key];
              if (element) {
                if (key.search('rice') > -1 && price && price.currency) {
                  Object.assign(initialValues, { [cd.key + '_' + key]: new Money(element, price.currency) });
                } else {
                  Object.assign(initialValues, { [cd.key + '_' + key]: element });
                }
              }
            }
          }
        }
        return cd;
      }
    });
    Object.assign(initialValues, { [configKey]: updatedData });
    return { initialValues };
  }

  if (data && Object.keys(data).length && data[configKey] && data[configKey].length && Array.isArray(data[configKey])) {
    let price = 0;
    const updatedData = data[configKey].map((st) => {
      const highlights = [], inputValues = (st.inputValues) || {};
      for (const key in data) {
        if (Object.hasOwnProperty.call(data, key)) {
          const newKeyName = key.split('_');
          if (newKeyName && newKeyName.length > 1 && st.key == newKeyName[0] && data[key]) {
            if (key != 'price' && key.search('rice') > -1) {
              if (data[key] && data[key].amount) {
                if (!price) {
                  price = data[key];
                } else if (price && price.amount && price.amount > data[key].amount) {
                  price = data[key];
                }
                Object.assign(inputValues, { price: (data[key].amount) });
              }
            } else if (key.search('highlights') > -1) {
              highlights.push(data[key]);
              Object.assign(inputValues, { [newKeyName[1]]: data[key] });
            } else {
              Object.assign(inputValues, { [newKeyName[1]]: data[key] });
            }
          }
        }
      }
      const d = ({
        ...st,
        inputValues
      });
      if (highlights && highlights.length) {
        Object.assign(d, { highlights });
      }
      return d;
    });
    return { [configKey]: updatedData, price };
  }
  return {};
}

// Copy to clipboards
export const copyToClipboard = text => {
  let dummy = document.createElement("textarea");
  document.body.appendChild(dummy);
  dummy.value = text;
  dummy.select();
  document.execCommand("copy");
  document.body.removeChild(dummy);
}

export const makeIntialvalue = (data, name) => {
  let extraIntialData = {};
  if (data && data.length) {
    data.map((val, i) => {
      extraIntialData[name + (i + 1)] = val;
    })
  }
  return extraIntialData;
}

// Get page url
export const getPageURL = () => {
  let pageUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
  if (typeof window !== 'undefined') {
    pageUrl += (window.location.pathname + window.location.search);
    return pageUrl;
  }
  return pageUrl;
}


export const downloadAsPDFChatgpt = (content, filename = 'Job_Description.pdf', title) => {
  const doc = new jsPDF();

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // --- Watermark Function ---
  function addWatermark() {
    doc.setFontSize(50);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(180, 180, 180);
    doc.text('FORTEH', pageWidth / 3.0, pageHeight / 2.0, { angle: 45 });
  }

  // --- First Page Watermark ---
  addWatermark();

  // --- Image + Title Setup ---
  const imageBase64 = listView; // Replace with your actual base64 image
  const imgWidth = 10;
  const imgHeight = 10;

  const titleText = `${title} Interview Questions`;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const textWidth = doc.getTextWidth(titleText);
  const totalWidth = imgWidth + 4 + textWidth;
  const startX = (pageWidth - totalWidth) / 2;
  const y = 18;

  doc.addImage(imageBase64, 'PNG', startX, y - imgHeight + 2, imgWidth, imgHeight);
  doc.link(startX, y - imgHeight + 2, imgWidth, imgHeight, {
    url: 'https://forteh.ca'
  });

  doc.setTextColor('#e57552');
  doc.text(titleText, startX + imgWidth + 4, y);

  // --- Set styles for body content after title ---
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);

  const lineHeight = 7;
  let yPosition = 40;

  // ✅ Pre-process content to enforce spacing between questions
  const preProcessedContent = content
    .split(/\n\s*\n?/)
    .map(q => q.trim())
    .filter(q => q.length > 0)
    .join('\n\n\n');

  const questions = preProcessedContent
    .split(/\n\s*\n/)
    .map(q => q.trim())
    .filter(q => q.length > 0);

  // --- Render questions ---
  questions.forEach((question) => {
    const wrapped = doc.splitTextToSize(question, 180);

    wrapped.forEach(line => {
      if (yPosition + lineHeight > pageHeight - 20) {
        doc.addPage();
        addWatermark();

        // ✅ Reapply text styles on new page
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);

        yPosition = 20;
      }
      doc.text(line, 10, yPosition);
      yPosition += lineHeight;
    });

    // Add spacing after each question
    yPosition += lineHeight * 1.5;
  });

  // --- Save and Return ---
  doc.save(filename);
  const blob = doc.output('blob');
  return blob;
};





export const uploadPDFToCloudinary = async (pdfBlob, publicId = 'interview_questions') => {
  const formData = new FormData();
  formData.append('file', pdfBlob);
  formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_PRESET);
  formData.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY);

  try {
    const res = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_NAME}/upload`, {
      method: 'POST',
      body: formData,
    });

    const data = await res.json();

    if (data.secure_url) {
      console.log('Uploaded PDF URL:', data.secure_url);
      return data.secure_url;
    } else {
      console.error('Upload failed:', data);
    }
  } catch (err) {
    console.error('Cloudinary upload error:', err);
  }
};
export const stringFromLength = (string, length, flag = false) => {
  const trimmedString = string.substr(0, length);
  return trimmedString.substr(0, Math.min(trimmedString.length, trimmedString.lastIndexOf(" "))) + (flag
    ? "..."
    : "");
}

export const getInitials = (fullName) => {
  console.log(fullName,'fullName');
  if(fullName && fullName.length){

    const names = fullName?.trim()?.split(' ');
    if (names?.length === 1) return names[0][0]?.toUpperCase();
    return (names[0][0] + names[names.length - 1][0])?.toUpperCase();
  }else{
    return null
  }
};

const cityCategoryMap = {
  toronto: "toronto", 
  scarborough: "toronto",
  etobicoke: "toronto",
  "north york": "toronto",
  "east york": "toronto",
  york: "toronto",
  mississauga: "toronto",
  brampton: "toronto",
  caledon: "toronto",
  vaughan: "toronto",
  markham: "toronto",
  "richmond hill": "toronto",
  aurora: "toronto",
  newmarket: "toronto",
  king: "toronto",
  "whitchurch-stouffville": "toronto",
  "east gwillimbury": "toronto",
  georgina: "toronto",
  pickering: "toronto",
  ajax: "toronto",
  whitby: "toronto",
  oshawa: "toronto",
  clarington: "toronto",
  uxbridge: "toronto",
  scugog: "toronto",
  brock: "toronto",
  oakville: "toronto",
  burlington: "toronto",
  milton: "toronto",
  "halton hills": "toronto",

  vancouver: "vancouver",
  burnaby: "vancouver",
  richmond: "vancouver",
  "north vancouver (city)": "vancouver",
  "north vancouver (district)": "vancouver",
  "west vancouver": "vancouver",
  "new westminster": "vancouver",
  surrey: "vancouver",
  coquitlam: "vancouver",
  "port coquitlam": "vancouver",
  "port moody": "vancouver",
  delta: "vancouver",
  "langley (city)": "vancouver",
  "langley (township)": "vancouver",
  "maple ridge": "vancouver",
  "pitt meadows": "vancouver",
  "white rock": "vancouver",

  calgary: "calgary",
  airdrie: "calgary",
  okotoks: "calgary",
  chestermere: "calgary",
  "high river": "calgary",
  strathmore: "calgary",
  crossfield: "calgary",
  irricana: "calgary",
  "turner valley": "calgary",
  "black diamond": "calgary"
};

export const extractCity = (address) => {
  if (!address) return "restOfCanada";
  const city = address?.split(",")[0].trim().toLowerCase();
  const matchedCity = Object.keys(cityCategoryMap).find(key => city?.includes(key));
  return matchedCity ? cityCategoryMap[matchedCity] : "restOfCanada";
};

export const getSuggestedRate = (roleKey, userLocation, parentJobRoles) => {
  const role = parentJobRoles?.find(r => r?.key === roleKey);
  const locationKey = extractCity(userLocation); 
  return role?.suggestedRates[locationKey];
}


export const getSuggestedRange = (rate) => {  
  const min = rate?.min;
  const max = rate?.max;

  return { min, max, label: `$${min}-${max}` };
};


export const roles = [
  {key:"ceo",label:"CEO",value:"ceo"},
  {key:"fractional",label:"Fractional Pro",value:"fractional"}
]

export const events = [
  {
    date: "24 September",
    time: "5:30 PM – 07:30 PM",
    location: "@ Calgary",
    role: "Fractional",
  },
  {
    date: "01 October",
    time: "5:30 PM – 07:30 PM",
    location: "@ Edmonton",
    role: "Fractional",
  },
  // {
  //   date: "08 January",
  //   time: "5:30 PM – 7:30 PM",
  //   location: "@ Winnipeg",
  //   role: "Fractional",
  // },
  // {
  //   date: "20 January",
  //   time: "5:30 PM – 7:30 PM",
  //   location: "@ Vancouver",
  //   role: "Fractional",
  // },
];


export const getDeviceName = () => {
  if (navigator.userAgentData) {
    const browser =
      navigator.userAgentData.brands.find(b => b.brand !== "Not A Brand")?.brand ||
      "Browser";
    const platform = navigator.userAgentData.platform;
    return `${platform} - ${browser}`;
  }

};